/**
 * The visitor's cookie-consent choice: what is stored, when it stops counting,
 * and how it combines with the Global Privacy Control signal. Pure and
 * framework-free; the React provider and the analytics loader build on it.
 */
import { addMonths } from 'date-fns';

export const COOKIE_CONSENT_STORAGE_KEY = 'belhop_cookie_consent';
export const COOKIE_CONSENT_VERSION = 1;
export const COOKIE_CONSENT_MAX_AGE_MONTHS = 12;

export interface CookieConsentRecord {
  version: number;
  analytics: boolean;
  /** ISO-8601 UTC timestamp of the decision. */
  decidedAt: string;
  /** Whether Global Privacy Control was on when the visitor decided. */
  gpc: boolean;
}

declare global {
  interface Navigator {
    /** Global Privacy Control; lib.dom does not type it. */
    readonly globalPrivacyControl?: boolean;
  }
}

const MAX_CLOCK_SKEW_MS = 24 * 60 * 60 * 1000;

const consentListeners = new Set<() => void>();

export function readStoredCookieConsentValue(): string | null {
  try {
    return window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function isRecordShape(value: unknown): value is CookieConsentRecord {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['version'] === 'number' &&
    typeof candidate['analytics'] === 'boolean' &&
    typeof candidate['decidedAt'] === 'string' &&
    typeof candidate['gpc'] === 'boolean'
  );
}

export function parseCookieConsentValue(raw: string | null, now: Date): CookieConsentRecord | null {
  if (raw === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecordShape(parsed) || parsed.version !== COOKIE_CONSENT_VERSION) {
    return null;
  }
  const decidedAt = new Date(parsed.decidedAt);
  if (Number.isNaN(decidedAt.getTime())) {
    return null;
  }
  if (decidedAt.getTime() - now.getTime() > MAX_CLOCK_SKEW_MS) {
    return null;
  }
  if (addMonths(decidedAt, COOKIE_CONSENT_MAX_AGE_MONTHS).getTime() <= now.getTime()) {
    return null;
  }
  return parsed;
}

function notifyConsentListeners(): void {
  consentListeners.forEach((listener) => listener());
}

/** Persists the record and tells subscribers; false when storage refused the write. */
export function writeCookieConsent(record: CookieConsentRecord): boolean {
  try {
    window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    return false;
  }
  notifyConsentListeners();
  return true;
}

export function readGlobalPrivacyControl(
  navigatorLike: Pick<Navigator, 'globalPrivacyControl'>,
): boolean {
  return navigatorLike.globalPrivacyControl === true;
}

/**
 * Whether analytics may run. Global Privacy Control wins over an opt-in made
 * before the signal was on; an opt-in made while it was on is honoured.
 */
export function resolveAnalyticsAllowed({
  record,
  gpc,
}: {
  record: CookieConsentRecord | null;
  gpc: boolean;
}): boolean {
  if (record === null) {
    return false;
  }
  return gpc ? record.analytics && record.gpc : record.analytics;
}

export function isConsentDecisionNeeded({
  record,
  gpc,
}: {
  record: CookieConsentRecord | null;
  gpc: boolean;
}): boolean {
  return record === null && !gpc;
}

/** Calls the listener when the choice changes here or in another tab; returns the unsubscribe function. */
export function subscribeToCookieConsent(listener: () => void): () => void {
  consentListeners.add(listener);
  const handleStorage = (event: StorageEvent): void => {
    if (event.key === COOKIE_CONSENT_STORAGE_KEY) {
      listener();
    }
  };
  window.addEventListener('storage', handleStorage);
  return () => {
    consentListeners.delete(listener);
    window.removeEventListener('storage', handleStorage);
  };
}
